"use client";
import React, { FC, Fragment, MouseEvent, ReactNode } from "react";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { faChevronDown } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";

import Button, { ButtonProps, getButtonStyles } from "@/components/button";

export type DropdownMenuEntry = {
  onClick?: (event: MouseEvent) => void;
  to?: string;
  icon?: IconDefinition;
  text: ReactNode;
  buttonProps?: Record<string, unknown>;
};

type DropDownMenuProps = {
  button: Omit<ButtonProps, "children"> & { text: string | ReactNode };
  items: DropdownMenuEntry[];
  align?: "left" | "right";
};

const DropdownMenu: FC<DropDownMenuProps> = ({
  button,
  items,
  align = "right",
  ...buttonProps
}) => {
  const renderEntry = (entry: DropdownMenuEntry, index: number) => {
    let component = null;

    if (entry.onClick) {
      component = (
        <Button
          type="menu"
          icon={entry.icon}
          onClick={entry.onClick}
          {...entry.buttonProps}
        >
          {entry.text}
        </Button>
      );
    } else if (entry.to) {
      component = (
        <Button
          type="menu"
          icon={entry.icon}
          href={entry.to}
          {...entry.buttonProps}
        >
          {entry.text}
        </Button>
      );
    }

    return <MenuItem key={index}>{component}</MenuItem>;
  };

  const menuAlign = align === "right" ? "right-0" : "left-0";

  return (
    <div className="relative inline-block text-left">
      <Menu>
        <MenuButton className={getButtonStyles(button)} {...buttonProps}>
          {button.icon ? <FontAwesomeIcon icon={button.icon} className="pr-2" /> : null}
          {button.text}
          <FontAwesomeIcon icon={faChevronDown} className="ml-1 opacity-50 text-xs" />
        </MenuButton>
        <MenuItems
          anchor="top start"
          transition
          className="bg-white focus:outline-none origin-top transition duration-200 ease-out data-closed:scale-95 data-closed:opacity-0 whitespace-nowrap rounded-md shadow-md"
        >
          {items.map(renderEntry)}
        </MenuItems>
      </Menu>
    </div>
  );
};

export default React.memo(DropdownMenu);
